// 騎士と花嫁の固有の致命の比較版 (2026-10-09・DEV の URL スイッチだけ・既定は今のまま)。
//
// 設計 = C:\dev\LUNAIRE-notes\assets\2026-10-09-exec-motion-art\knight-bride-finisher-design.md (§0-2 の 10-09 の答えが優先)。
//   騎士「背向けの一閃」: 背を向けて扉の方へ歩き去る → 背を向けたまま紫の止まり → 振り向きの途中 → 腰の高さで横一文字に振り抜く。
//     穂先の先から紫を帯びた冷たい白の光の帯が約 50ms で騎士の中心から 420px まで伸びる。当たりの前端 = 光の帯の前端 + 15px。
//   花嫁「おかえりの抱擁」: ヴェールを整えて♡ → 弾みながら滑り寄り、弾むたびに♡ → 目の前で紫の止まり (残った♡は弾けて光の粒に)
//     → 頭から胸へ飛び込む → 当たった後は今の exec_cover の抱擁。
//   ユーザーの答え (10-09): 騎士の斬撃 =「A 光の帯」/ ♡ =「近寄ったら灰色にする必要はない」/ 髪飾り = とどめの絵に出さない / 傷 =「60のまま」。
//
// 効く条件: ?execUnique=1 と、致命を弾ける仕組み (既定で効く・DEV ?execParry=off で切れる) が両方ある時だけ (崩れは致命が当たるか弾かれるまで続く前提)。
//   スイッチは game.ts の DEV の箱の中でだけ読む。配布版は URL を読まない = 騎士だけ固有の致命 (2026-10-12 K-5 から・下の ExecUniqueMode)。
// 作り: 覚醒王の「灰を食む」(src/awoken_devour.ts) と同じ形。致命に入る時に「この回の段取り」を決めて技の写しに持たせる (AttackDef.uniqueExec)。
//   id は今の 'knightExecution' / 'brideExecution' のまま (傷 60・弾きの仕組み・当たった後の流れはそのまま効く)。
//   絵の並び (タイムライン) と体の位置・向きは、その段取りから毎回作る。全ボス共通の部品 (一瞬の静止 120ms など) は触らず、
//   この2体の経路の中だけで「止まりの入り」に静止を置く (game.ts)。
import { execParryRecoilNow } from './exec_parry_recoil'
import type { AttackDef, Boss, FxEvent, Player } from './types'
import type { BossAttackTimeline, BossPoseKey } from './boss_pose_timeline'
import { execParryEnabled } from './exec_parry'
import { BOSS_EXECUTE_APPROACH_PX } from './balance'
import { knightWalkHelmetPx } from './knight_gait'
import { stunCutOn } from './stun_cut'

// 2026-10-12 (検収パック 10-12 のユーザー答え K-5「直してから」→ K-3・K-4 を直して): 騎士の固有のとどめ (突進) を配布版の既定にした。
//   花嫁は入れない (B-2「直してから」= 弾かれた時の絵と動きを作り直し中・開発版 ?execUnique=1 の中だけ)。
//   knight = 既定 (配布版): 騎士だけ固有のとどめ / all = ?execUnique=1: 騎士と花嫁 / off = ?execUnique=0: 前の既定 (どちらも固有のとどめなし)。
//   URL は game.ts の DEV の箱の中でだけ読む (配布版は URL を読まない = いつも knight)。
//   2026-10-14 (試遊パック next-play-1014 の F-1〜F-4 が全部よい): 弾かれた花嫁も配布版へ = 既定を all にした。
//   ?execUnique=knight = 10-12〜10-14 の配布版 (騎士だけ)・=0 = その前 (どちらもなし)・=1 = 既定と同じ。
export type ExecUniqueMode = 'knight' | 'all' | 'off'
let mode: ExecUniqueMode = 'all'
/** URL の値から切り替える ('knight' = 騎士だけ (10-14 までの配布版)・'0' / 'off' = 前の既定 (固有のとどめなし)・それ以外 = 既定の騎士と花嫁)。 */
export function setExecUnique(value: string | null | undefined): ExecUniqueMode {
  mode = value === 'knight' ? 'knight' : value === '0' || value === 'off' ? 'off' : 'all'
  return mode
}

// 騎士の壁ぎわの駆け抜け (10-12 K-4) の比べ (?execUniqueWall=pass)。指定なし = 駆け抜けない (その場で背を向けて短く突っ込む = 10-12 朝までの形)。
//   10-14 試遊 E-2「前のほうが良いな。向き直った後の新しい絵はダサい」: 脇を駆け抜けて反対側で背を向け直す形をやめ、前の形へ戻した。
let knightWallPass = false
export function setExecUniqueWall(value: string | null | undefined): boolean {
  knightWallPass = value === 'pass'
  return knightWallPass
}
/** 固有の致命がそのボスに効いているか (致命を弾ける仕組みが効いている時だけ)。bossId なし = どちらかに効いているか。 */
export function execUniqueOn(bossId?: string): boolean {
  if (mode === 'off' || !execParryEnabled()) return false
  if (bossId === undefined) return true
  return bossId === 'vigil-knight' || (mode === 'all' && bossId === 'bride')
}

// 騎士の紫の止まりの長さの比べ (?execUniqueHold=300|400|500)。指定なし = 設計図の 400ms。
export type UniqueHoldMs = 300 | 400 | 500
let holdOverride: UniqueHoldMs | null = null
export function setExecUniqueHold(value: string | null | undefined): UniqueHoldMs | null {
  holdOverride = value === '300' ? 300 : value === '400' ? 400 : value === '500' ? 500 : null
  return holdOverride
}

// ♡の大きさの比べ (?execHeartPx=12|14|16)。指定なし = 16 (10-12 ユーザー A-3「16px（推奨）」・前の既定は 14)。
export type HeartPx = 12 | 14 | 16
let heartPx: HeartPx = 16
export function setExecHeartPx(value: string | null | undefined): HeartPx {
  heartPx = value === '12' ? 12 : value === '14' ? 14 : 16
  return heartPx
}
export function execHeartPx(): HeartPx { return heartPx }

// 騎士の戻り方の比べ (?execUniqueRecover=guard|turn・10-09 夜の点検)。指定なし = guard。
//   guard = 振り抜いた K3 → 槍を主人公へ向けたまま引く (walk_reach) → 待機。槍は「前・水平」→「前・水平」→「縦」と1方向にしか動かない。
//   turn  = 前の版。K3 → 扉の方へ背を向ける (turn) → 待機。1コマで両足が 66・83px 跳び、槍が「前・水平」→「後ろ・斜め上」→「縦」と2回向きを変える。
export type KnightRecover = 'guard' | 'turn'
let knightRecover: KnightRecover = 'guard'
export function setExecUniqueRecover(value: string | null | undefined): KnightRecover {
  knightRecover = value === 'turn' ? 'turn' : 'guard'
  return knightRecover
}

// 騎士の形の比べ (?execUniqueKnight=band・2026-10-12)。指定なし = charge。
//   charge = 新 (10-12 ユーザー A-1 メモ「槍で弾いてると言うよりは衝撃波で攻撃をしてるから、とんでもない突進みたいに」):
//            背を向けて離れる → 紫の止まり (400ms) → 振り向き → 槍を腰に構えて一気に突っ込む。光は騎士と穂先に付いて一緒に来る
//            (穂先から後ろへ伸びる光の尾)。穂先が主人公に届いたコマが当たり。弾かれたら突っ込んだ勢いのまま穂先で弾かれてのけぞる。
//   band   = 前の版 (10-09): 離れた所で振り抜き、光の帯が 50ms で 420px まで飛ぶ。
export type KnightExecForm = 'charge' | 'band'
let knightForm: KnightExecForm = 'charge'
export function setExecUniqueKnight(value: string | null | undefined): KnightExecForm {
  knightForm = value === 'band' ? 'band' : 'charge'
  return knightForm
}
export function execUniqueKnightForm(): KnightExecForm { return knightForm }

// 花嫁の弾かれた後の比べ (?execUniqueBrideRise=glide|old)。指定なし = snap。
//   snap  = 新 (10-12 ユーザー B-2「弾かれた時専用の絵が欲しい。あと、弾かれたって動きには見えない。ゆったり傾斜していってる感じなのがしっくりきてない」):
//           弾かれた専用の絵 exec_parried_snap_1012 (上体が跳ね起きて少し反り、腕・袖・ヴェールが前へあおられる) へ替え、
//           0.11 秒で頭が約 45px 跳ね返る (速い動き) → 0.2 秒止める → 0.4 秒で待機へ戻る。下がる距離は glide と同じくらい (B-1「ちょうどいい」)。
//   glide = 10-12 朝の版 (A-5「強すぎる」の直し): 飛び込み → 寄っていく絵 (腕とヴェールが後ろへ流れる) のまま上体が 0.45 秒かけてゆっくり起きる。
//   old   = 前の版 (10-09 夜): 飛び込み → 弾かれた姿 (parried・上体を大きく後ろへ反らす) → 待機。
export type BrideRise = 'snap' | 'glide' | 'old'
let brideRise: BrideRise = 'snap'
export function setExecUniqueBrideRise(value: string | null | undefined): BrideRise {
  brideRise = value === 'old' ? 'old' : value === 'glide' ? 'glide' : 'snap'
  return brideRise
}
export function execUniqueBrideRise(): BrideRise { return brideRise }

// ================================ 騎士 ================================

/** 新しいキーポーズ (10-09 Codex・窓口が合格・scripts/chroma_extract.py で抜いて bbox で切った物)。 */
export const KNIGHT_EXEC_ART = {
  /** K1 背を向けたまま紫の止まり (絵は画面右を向く = 主人公へ背中)。 */
  backcoil: 'exec_backcoil_1009',
  /** K2 振り向きの途中 (体は正面・兜は主人公・穂先はまだ後ろ)。 */
  turnmid: 'exec_turnmid_1009',
  /** K3 振り抜き = 当たり (槍が腰の高さ)。raw/knight_backturn_v3.png を抜いた物。 */
  backturn: 'exec_backturn_1009',
} as const

// 3枚の置き場 (10-09 色合わせ): 直下 public/sprites/bosses/vigil-knight/<key> (色合わせ前 = ?knightColor=original) と graded/<key> (既定)。
//   槍は今の騎士の槍 (横向きの絵 7枚で 198〜214px) に合わせて 212px・穂先〜口金 50px へ縮めた (体は動かしていない・画像の寸法も同じ)。
//   作り方 = scripts/knight_exec_unique_art.py・縮める前の元 = public/sprites/archived/vigil-knight/exec-unique-1009-unedited/。
/** 新しい3枚の描き方 (sprites.ts resolveBossSpriteArtPlacement)。騎士は面積で大きさを決めるが、この3枚は槍とマントが長く面積が大きいので
 *  兜の大きさで合わせた固定の倍率にする。pxScale = 表示 216px の時の 1 画素あたりの大きさ。
 *  決め方: 兜の高さと幅を今の絵 (lanceSweep_contact = 兜 190px で 0.1703・turn = 背中の兜) と比べた (2026-10-09・目測込み・誤差 ±5%)。
 *  anchorX = 体の中心 (腰と両足の真ん中) の画素の x。これがボスの x に立つ。tip = K3 の穂先の先端の画素。 */
export const KNIGHT_EXEC_ART_PLACEMENT: Readonly<Record<string, { pxScale: number; anchorX: number }>> = {
  [KNIGHT_EXEC_ART.backcoil]: { pxScale: 0.1885, anchorX: 648 },
  [KNIGHT_EXEC_ART.turnmid]: { pxScale: 0.1795, anchorX: 484 },
  [KNIGHT_EXEC_ART.backturn]: { pxScale: 0.1834, anchorX: 926 },
}
/** K3 の穂先の先端 (絵の画素)。光の帯はここから伸びる。10-09 槍を縮めて x 0 → 187 (画像の寸法は同じ・穂先が体の方へ寄った)。 */
export const KNIGHT_EXEC_TIP_SRC = { x: 187, y: 358 } as const
/** K3 の穂先の高さ (床から px・表示 216px)。不透明の下端 726 から穂先 358 まで × 0.1834。弾いた光もこの高さに出す (hits.ts)。 */
export const KNIGHT_EXEC_BLADE_HEIGHT_PX = (726 - KNIGHT_EXEC_TIP_SRC.y) * 0.1834
/** K3 の穂先の先端の、ボスの中心からの前への距離 (表示 216px・倍率 1)。(926 − 187) × 0.1834 ≒ 136 (縮める前は 170)。 */
export const KNIGHT_EXEC_TIP_FWD_PX = (KNIGHT_EXEC_ART_PLACEMENT[KNIGHT_EXEC_ART.backturn]!.anchorX - KNIGHT_EXEC_TIP_SRC.x)
  * KNIGHT_EXEC_ART_PLACEMENT[KNIGHT_EXEC_ART.backturn]!.pxScale

/** 突進の走りの絵 (今ある壁ぎわの突進 knightWallCharge の2枚) の穂先の先端 (絵の画素・不透明の一番前)。光の尾の前端はここ。 */
export const KNIGHT_CHARGE_TIP_SRC: Readonly<Record<string, { x: number; y: number }>> = {
  charge_run1: { x: 8, y: 379 },
  charge_run2: { x: 11, y: 384 },
  [KNIGHT_EXEC_ART.backturn]: KNIGHT_EXEC_TIP_SRC,
}

/** 騎士の突進 (?execUniqueKnight 指定なし = charge・2026-10-12)。数値は tests/exec_unique_measure.test.ts で絵の位置を測って決めた。 */
export const KNIGHT_CHARGE = {
  /** 止まる時の間合いの目標 (突進の助走)。band の 360 より近い = 歩く量を減らして崩れから当たりまでを 1.8 秒以内に収める。 */
  holdGapPx: 330,
  /** 歩きは最大 2 歩 (1歩 77px = 今の歩幅)・少し早足 (190px/秒・運ぶ 170ms は今の歩きと同じ = 足は滑らない)。 */
  maxSteps: 2,
  /** 崩れたら打ち切る (既定・src/stun_cut.ts) 時は 1 歩だけ: 技の途中で崩れた時の戻り (0.2 秒) を足しても崩れから当たりまで 1.8 秒に収める
   *  (2 歩だと間合い 120 で 1.82 秒・技の途中で 2.08 秒だった・10-12)。旧 (DEV ?stunCut=0) は 2 歩。 */
  maxStepsCut: 1,
  walkSpeedPxPerSec: 190,
  /** 振り向きの途中 (K2)。band の 220 より短い (振り向いたらすぐ走り出す)。 */
  turnMidMs: 160,
  /** 走り: 平均 1100px/秒。進み方 s(p) = p × (v0 + (1 − v0) p) = 走り出し 440px/秒 → 当たる瞬間 1760px/秒 (加速しながら突っ込む)。 */
  avgSpeedPxPerSec: 1100,
  v0Share: 0.4,
  minRunMs: 110,
  /** 走りの絵 (run1 ↔ run2) を替える間隔 (進んだ距離)。 */
  strideImagePx: 90,
  /** 当たり = K3 (振り抜き = 腰だめの突き) の穂先が主人公の中心を 6px 越えた所 (体の中に届いている)。 */
  contactOverlapPx: 6,
  /** 当たった後: 勢いで K3 のまま 16px 前へ滑って止まる (120ms・腰を落とした構え = 滑ってよい絵)。 */
  brakePx: 16,
  brakeMs: 120,
  /** 弾かれた後: 穂先で弾き返されて 44px 後ろへのけぞりながら下がる (260ms)。 */
  recoilPx: 44,
  recoilMs: 260,
  /** 光の尾 (穂先から後ろへ): 長さ = 走り出しの 260px + 走った距離 × 1.5 (最大 520px)。
   *  10-12 ユーザー K-3「体の後ろまで長く見せる」: 前は 110px + 走った距離 (最大 320px) で、穂先から騎士の背中まで (約 250px) に
   *  ほとんど収まり、外套に隠れて体の後ろへはほぼ出ていなかった。いまは当たる時に背中の後ろへ約 150〜250px 出る (測定 = tests/exec_unique_measure.test.ts)。 */
  wakeBasePx: 260,
  wakeTravelMul: 1.5,
  wakeMaxPx: 520,
  /** 光の尾から落ちて、通った所に少し残る光の粒 (火の粉のように散らす・線にしない)。生まれてから消えるまで。 */
  wakeSparkLifeMs: 300,
  /** 走り出してから光の尾が灯り切るまで。 */
  wakeFadeInMs: 50,
} as const

/** 背中が壁ぎわの時 (10-12 ユーザー K-4「壁ぎわは内側へ寄って助走を取る」): 後ろへ歩けず助走が短くなる時は、
 *  主人公の脇を駆け抜けて部屋の内側へ回り、背を向けて止まる → 振り向いて壁の方へ突っ込む (助走は普段とほぼ同じ)。
 *  駆け抜けは今ある壁ぎわの突進 (knightWallCharge・追い詰められると主人公の脇を走り抜ける) と同じ走りの絵 2 枚。光と当たりは無い。 */
export const KNIGHT_WALL_PASS = {
  /** 普段どおり段取りを組んで、走る距離がこれより短く、しかも背中側の壁で歩けなかった時だけ駆け抜ける。 */
  triggerRunPx: 100,
  /** 駆け抜けた先の間合いの目標と下限 (主人公の中心から)。普段の突進 (330 − 12) とほぼ同じ助走になる。 */
  toGapPx: 280,
  minToGapPx: 240,
  /** 駆け抜けの平均の速さ (px/秒): 普段 560 (壁ぎわの突進の走りと同じくらい)・急ぐ時も 780 まで。 */
  speedPxPerSec: 560,
  maxSpeedPxPerSec: 780,
  /** 崩れてから当たるまで 1.8 秒の中に収めるための、溜め (段取り全体) の上限。
   *  崩れから溜めの頭まで + 止まりの入りの一瞬の静止 (120ms) で約 160ms (待機から・測った)・技の途中で崩れた時の戻り 0.2 秒を見込む。 */
  budgetMs: 1440,
  /** 駆け抜けの頭: 待機の絵から走りの絵へ替わる前の一瞬 (床を蹴る溜め)。 */
  kickoffMs: 40,
  /** 進んだ距離のこの割合から踏ん張りの絵 (scrape_stop) で滑って止まる。 */
  brakeShare: 0.8,
} as const

export const KNIGHT_BACKTURN = {
  /** 背を向ける (idle → turn)。turn の絵は画面右を向いて立つ = 主人公へ背中。 */
  turnMs: 250,
  turnImageAtMs: 70,
  /** 止まる時の騎士の中心と主人公の中心の間 (目標)。 */
  holdGapPx: 360,
  /** これより遠ければ歩き去らずその場で背を向ける・480 より遠ければ先に主人公の方へ歩く。 */
  approachIfFartherPx: 480,
  approachToPx: 400,
  /** 歩き: 今の歩きの速さ (148px/秒) で、1歩 (最大 77px = 今の歩幅) ずつ。運ぶ 60%・置く 40%。最大 3 歩。 */
  walkSpeedPxPerSec: 148,
  maxStridePx: 77,
  carryMs: 170,
  lastRestMs: 150,
  maxSteps: 3,
  minWalkPx: 20,
  /** 紫の止まり (設計図 400ms・?execUniqueHold で 300/500 と比べる)。 */
  holdMs: 400,
  /** 振り向きの途中 (K2) → 当たりまで。
   *  10-09 夜: 前は 220ms で体ごと 60px 前へ滑らせていた (両足が約 70px 床を滑る)。K2 の絵は「奥の足を軸に回り、手前の足を浮かせて踏み出す」形なので、
   *  軸足 (主人公から遠い足) を K1 → K2 → K3 で床に残す: K2 の間は体を動かさず、K3 で浮いていた手前の足が着く。
   *  K3 の軸足は K2 の軸足より体の中心から 12px 遠いので、その分だけ前へ出る (12px = 軸足が動かない量・tests/exec_unique_measure.test.ts で測った)。 */
  turnMidMs: 220,
  turnMidSlidePx: 12,
  /** 光の帯: 騎士の中心から 420px まで、約 50ms で伸びる。当たりの前端 = 光の帯の前端 + 15。 */
  bladeReachPx: 420,
  bladeGrowMs: 50,
  reachMarginPx: 15,
  /** 当たりの後端 (今の knightExecution.strike と同じ)。 */
  strikeFromPx: 45,
  /** 光の帯が消えるまで (当たりから)・床の残り火 (当たりから)。 */
  bladeFadeStartMs: 70,
  bladeEndMs: 150,
  embersStartMs: 30,
  embersEndMs: 650,
  /** 主人公との間がこれより近くならない (踏み込みの下限)。 */
  minGapPx: 120,
  /** 背中側で絵が一番張り出す所 (K2 の外套の端・体の中心から px)。カメラが止まっている部屋では、止まる所をこの分だけ画面の端から離す
   *  (10-09 夜: 右の壁ぎわで K2 の外套と穂先が最大 25px 画面の外で切れていた・boss.ts)。 */
  screenBackExtentPx: 152,
} as const

export type ExecUniqueStep = {
  startMs: number
  carryEndMs: number
  endMs: number
  fromGapPx: number
  toGapPx: number
  /** 主人公と逆へ歩く (背を向けて歩き去る)。false = 主人公の方へ歩く。 */
  away: boolean
}

export type KnightBackturnPlan = {
  boss: 'vigil-knight'
  startGapPx: number
  steps: ExecUniqueStep[]
  turnStartMs: number
  holdStartMs: number
  holdMs: number
  holdGapPx: number
  turnMidStartMs: number
  strikeGapPx: number
  chargeMs: number
  /** 形 (?execUniqueKnight)。charge = 突進 (既定)・band = 前の版の光の帯。 */
  form: KnightExecForm
  /** charge だけ: 走り出す時刻 (溜めの頭から ms)・走り出す時の間合い。当たる時の間合い = strikeGapPx。 */
  runStartMs: number
  runFromGapPx: number
  /** 背中が壁ぎわの時だけ (KNIGHT_WALL_PASS): 0〜endMs で主人公の脇を駆け抜けて反対側の toGapPx へ。この後の間合いは反対側で数える。 */
  pass?: { startMs: number; endMs: number; toGapPx: number }
  /** 致命の当たりをジャストで弾かれた (hits.ts が当たりの瞬間に立てる・絵の並びを「弾かれた姿」へ替える)。 */
  parried?: boolean
}

function planSteps(startMs: number, fromGap: number, toGap: number, away: boolean,
  walk: { maxSteps: number; walkSpeedPxPerSec: number } = KNIGHT_BACKTURN): ExecUniqueStep[] {
  const K = KNIGHT_BACKTURN
  const dist = Math.abs(toGap - fromGap)
  if (dist < K.minWalkPx) return []
  // 今の歩き (src/knight_gait.ts の既定 open: 1歩 = 広い構え walk_reach で 170ms で運び、揃えかけ walk_passing で止まる) と同じ拍。
  //   1歩の周期 = 歩幅 ÷ 148px/秒 (77px なら 520ms)。運ぶ時間は歩幅の平方根で縮める (今の歩きと同じ)。最後の1歩の後は短く止まって止まりへ。
  //   突進 (charge) は最大 2 歩・190px/秒 (運ぶ時間は同じ・置いて止まる間だけ短い)。
  const n = Math.min(walk.maxSteps, Math.max(1, Math.round(dist / K.maxStridePx)))
  const stride = dist / n
  const carryMs = Math.max(90, Math.round(K.carryMs * Math.sqrt(stride / K.maxStridePx)))
  const periodMs = Math.round(stride / walk.walkSpeedPxPerSec * 1000)
  const sign = Math.sign(toGap - fromGap)
  const out: ExecUniqueStep[] = []
  let t = startMs, g = fromGap
  for (let i = 0; i < n; i++) {
    const to = g + sign * stride
    const last = i === n - 1
    const endMs = t + Math.max(carryMs + 60, last ? carryMs + K.lastRestMs : periodMs)
    out.push({ startMs: t, carryEndMs: t + carryMs, endMs, fromGapPx: g, toGapPx: to, away })
    t = endMs
    g = to
  }
  return out
}

/** 騎士の段取り。startGapPx = いまの間合い、roomBehindPx = 騎士の背中側の壁までの残り (それより遠くへは歩けない)。 */
export function planKnightBackturn(startGapPx: number, roomBehindPx: number, form: KnightExecForm = knightForm,
  roomPastPlayerPx = Infinity): KnightBackturnPlan {
  const plan = planKnightBackturnInPlace(startGapPx, roomBehindPx, form)
  if (form !== 'charge' || !knightWallPass) return plan
  // 背中が壁ぎわで後ろへ歩けず、走る距離が短い: 主人公の脇を駆け抜けて内側から突っ込む (K-4)。
  const P = KNIGHT_WALL_PASS
  const runPx = plan.runFromGapPx - plan.strikeGapPx
  const wallLimited = roomBehindPx < KNIGHT_CHARGE.holdGapPx - startGapPx
  if (runPx >= P.triggerRunPx || !wallLimited) return plan
  return planKnightWallPass(startGapPx, holdMsNow(), roomPastPlayerPx) ?? plan
}

function holdMsNow(): number { return holdOverride ?? KNIGHT_BACKTURN.holdMs }

/** 壁ぎわの駆け抜けの段取り。反対側に場所が無ければ null (普段の段取りのまま)。 */
export function planKnightWallPass(startGapPx: number, holdMs: number, roomPastPlayerPx: number): KnightBackturnPlan | null {
  const P = KNIGHT_WALL_PASS
  const C = KNIGHT_CHARGE
  const K = KNIGHT_BACKTURN
  const strikeGapPx = KNIGHT_EXEC_TIP_FWD_PX - C.contactOverlapPx
  const runMsFor = (runPx: number) => runPx > 1 ? Math.max(C.minRunMs, Math.round(runPx / C.avgSpeedPxPerSec * 1000)) : 0
  // 遠い目標から順に、時間に収まる (速すぎない) 所を探す。
  for (let toGap = Math.min(P.toGapPx, roomPastPlayerPx); toGap >= P.minToGapPx; toGap -= 10) {
    const runFromGapPx = toGap - K.turnMidSlidePx
    const runMs = runMsFor(runFromGapPx - strikeGapPx)
    const dist = startGapPx + toGap
    // 普段の速さ (560px/秒) で走る。間に合わなければ上限 (760px/秒) の中で詰める。それでも間に合わなければ次の近い目標へ。
    const fit = P.budgetMs - holdMs - C.turnMidMs - runMs - P.kickoffMs
    const ms = Math.min(Math.round(dist / P.speedPxPerSec * 1000), fit)
    if (ms <= 0 || dist / (ms / 1000) > P.maxSpeedPxPerSec + 1e-6) continue
    const startMs = P.kickoffMs
    const endMs = startMs + ms
    const holdStartMs = endMs
    const turnMidStartMs = holdStartMs + holdMs
    const runStartMs = turnMidStartMs + C.turnMidMs
    return { boss: 'vigil-knight', form: 'charge', startGapPx, steps: [], turnStartMs: -1, holdStartMs, holdMs, holdGapPx: toGap,
      turnMidStartMs, strikeGapPx, chargeMs: runStartMs + runMs, runStartMs, runFromGapPx, pass: { startMs, endMs, toGapPx: toGap } }
  }
  return null
}

/** 駆け抜けの進み方 (0..1 → 0..1)。止まった所から蹴り出し、走り、背を向けて止まる所で踏みとどまる (前後対称の加減速)。 */
export function knightPassShare(p: number): number {
  const q = Math.max(0, Math.min(1, p))
  return q * q * (3 - 2 * q)
}

function planKnightBackturnInPlace(startGapPx: number, roomBehindPx: number, form: KnightExecForm): KnightBackturnPlan {
  const K = KNIGHT_BACKTURN
  const C = KNIGHT_CHARGE
  const charge = form === 'charge'
  const holdMs = holdOverride ?? K.holdMs
  const walk = charge ? { maxSteps: knightChargeMaxSteps(), walkSpeedPxPerSec: C.walkSpeedPxPerSec } : K
  let steps: ExecUniqueStep[]
  let turnStartMs: number
  let holdStartMs: number
  let holdGap: number
  if (startGapPx > K.approachIfFartherPx) {
    // 遠すぎる (光の帯が届かない・突進の助走が長すぎる): 先に主人公の方へ歩いてから、その場で背を向ける。
    steps = planSteps(0, startGapPx, K.approachToPx, false, walk)
    turnStartMs = steps.length ? steps[steps.length - 1]!.endMs : 0
    holdStartMs = turnStartMs + K.turnMs
    holdGap = steps.length ? steps[steps.length - 1]!.toGapPx : startGapPx
  } else {
    // 背を向けて扉の方へ。背中側の壁の手前で止まる (届かなくても光の帯は 420px まで伸びるので当たる・突進は助走が短くなるだけ)。
    turnStartMs = 0
    const target = charge ? C.holdGapPx : K.holdGapPx
    const maxWalk = charge ? walk.maxSteps * K.maxStridePx : Infinity
    const want = Math.max(0, Math.min(target - startGapPx, Math.max(0, roomBehindPx), maxWalk))
    steps = planSteps(K.turnMs, startGapPx, startGapPx + want, true, walk)
    holdStartMs = steps.length ? steps[steps.length - 1]!.endMs : K.turnMs
    holdGap = steps.length ? steps[steps.length - 1]!.toGapPx : startGapPx
    // 背中側の余裕が負 (もう画面の端に近すぎる・boss.ts): 歩かず、止まりの入り (K1 へ構える一瞬の静止) で内側へ寄せる。
    if (roomBehindPx < 0) holdGap = Math.max(K.minGapPx, startGapPx + roomBehindPx)
  }
  const turnMidStartMs = holdStartMs + holdMs
  if (!charge) {
    const chargeMs = turnMidStartMs + K.turnMidMs
    const strikeGapPx = Math.max(K.minGapPx, Math.min(holdGap, holdGap - K.turnMidSlidePx))
    return { boss: 'vigil-knight', form, startGapPx, steps, turnStartMs, holdStartMs, holdMs, holdGapPx: holdGap, turnMidStartMs,
      strikeGapPx, chargeMs, runStartMs: chargeMs, runFromGapPx: strikeGapPx }
  }
  // 突進: K2 で軸足を残して 12px 前へ → 走って K3 の穂先が主人公の中心を 6px 越える間合いへ。もう近すぎる時はその場で突く (走らない)。
  const runStartMs = turnMidStartMs + C.turnMidMs
  const runFromGapPx = Math.max(K.minGapPx * 0.5, holdGap - K.turnMidSlidePx)
  const strikeGapPx = Math.min(runFromGapPx, KNIGHT_EXEC_TIP_FWD_PX - C.contactOverlapPx)
  const runPx = runFromGapPx - strikeGapPx
  const runMs = runPx > 1 ? Math.max(C.minRunMs, Math.round(runPx / C.avgSpeedPxPerSec * 1000)) : 0
  return { boss: 'vigil-knight', form, startGapPx, steps, turnStartMs, holdStartMs, holdMs, holdGapPx: holdGap, turnMidStartMs,
    strikeGapPx, chargeMs: runStartMs + runMs, runStartMs, runFromGapPx }
}

/** 突進の進み方 (0..1 → 0..1)。走り出し 0.4 倍の速さから、当たる瞬間 1.6 倍まで加速する。 */
/** 突進の歩き去る歩数の上限 (既定 = 1 歩・旧 DEV ?stunCut=0 = 2 歩)。 */
export function knightChargeMaxSteps(): number {
  return stunCutOn() ? KNIGHT_CHARGE.maxStepsCut : KNIGHT_CHARGE.maxSteps
}

export function knightChargeRunShare(p: number): number {
  const q = Math.max(0, Math.min(1, p))
  const a = KNIGHT_CHARGE.v0Share
  return q * (a + (1 - a) * q)
}
/** knightChargeRunShare の逆 (進んだ割合 → 時間の割合)。走りの絵を替える時刻に使う。 */
function knightChargeRunShareInv(s: number): number {
  const a = KNIGHT_CHARGE.v0Share
  const b = 1 - a
  return (-a + Math.sqrt(a * a + 4 * b * Math.max(0, Math.min(1, s)))) / (2 * b)
}

/** knightPassShare の逆 (進んだ割合 → 時間の割合・二分法)。 */
function knightPassShareInv(s: number): number {
  let lo = 0, hi = 1
  for (let i = 0; i < 30; i++) { const m = (lo + hi) / 2; if (knightPassShare(m) < s) lo = m; else hi = m }
  return (lo + hi) / 2
}

/** 突進の走りの絵 (溜めの頭から ms)。走る前・当たってからは undefined。 */
export function knightChargeRunImageAt(plan: KnightBackturnPlan, ms: number): 'charge_run1' | 'charge_run2' | undefined {
  if (plan.form !== 'charge' || ms < plan.runStartMs || ms >= plan.chargeMs) return undefined
  const runPx = plan.runFromGapPx - plan.strikeGapPx
  const p = (ms - plan.runStartMs) / (plan.chargeMs - plan.runStartMs)
  const traveled = runPx * knightChargeRunShare(p)
  return Math.floor(traveled / KNIGHT_CHARGE.strideImagePx) % 2 === 0 ? 'charge_run1' : 'charge_run2'
}

/** 突進の足元の砂埃 (game.ts から毎 tick・描くだけ)。今ある騎士の踏み込みの砂埃 (knight_lunge_dust.ts・09-27 ユーザー「前に蹴り出しているような砂埃」) を使う。
 *  走り出し = K2 の後ろ足から強く蹴る・走りの絵が替わるたび = その絵の床に着いた足から小さく蹴る・当たって滑る間 = 後ろ足の擦り跡。
 *  足の位置 (体の中心から前へ +px) は tests/exec_unique_measure.test.ts で絵の接地点を測った値。 */
const KNIGHT_CHARGE_FOOT_FWD_PX: Readonly<Record<string, number>> = {
  [KNIGHT_EXEC_ART.turnmid]: -79, charge_run1: 62, charge_run2: -60, [KNIGHT_EXEC_ART.backturn]: -88,
}
export function knightChargeDustFx(prev: Boss, next: Boss, now: number): FxEvent[] {
  const plan = next.currentAttack?.uniqueExec
  if (next.id !== 'vigil-knight' || !plan || plan.boss !== 'vigil-knight' || plan.form !== 'charge') return []
  const fwd = next.facing === 'right' ? 1 : -1
  const out: FxEvent[] = []
  const kick = (image: string, amount: number) => out.push({ kind: 'knight_lunge_dust', variant: 'kick',
    x: next.x + fwd * (KNIGHT_CHARGE_FOOT_FWD_PX[image] ?? 0), y: next.y, t: now, dir: fwd, amount })
  if (next.state === 'telegraph' && prev.state === 'telegraph' && prev.currentAttack?.uniqueExec === plan) {
    const a = knightChargeRunImageAt(plan, prev.stateElapsed * 1000)
    const b = knightChargeRunImageAt(plan, next.stateElapsed * 1000)
    if (b && !a) kick(KNIGHT_EXEC_ART.turnmid, 0.95)
    else if (b && a !== b) kick(b, 0.6)
    // 壁ぎわの駆け抜け (K-4): 蹴り出しと一歩ごとの小さい砂埃 (突進より少し弱い)。
    const pa = knightPassRunImageAt(plan, prev.stateElapsed * 1000)
    const pb = knightPassRunImageAt(plan, next.stateElapsed * 1000)
    if (pb && !pa) kick(pb, 0.75)
    else if (pb && pa !== pb) kick(pb, 0.45)
  }
  if (next.state === 'active' && prev.state === 'active' && !plan.parried) {
    const moved = next.x - prev.x
    if (Math.abs(moved) > 0.3 && Math.sign(moved) === fwd) {
      const rear = KNIGHT_CHARGE_FOOT_FWD_PX[KNIGHT_EXEC_ART.backturn]!
      out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: prev.x + fwd * rear, x2: next.x + fwd * rear, y: next.y, t: now, dir: fwd, amount: 0.7 })
    }
  }
  return out
}

/** 突進の光の尾の長さ (穂先から後ろへ px)。traveledPx = 走った距離。 */
export function knightWakeLengthPx(traveledPx: number): number {
  const C = KNIGHT_CHARGE
  return Math.min(C.wakeMaxPx, C.wakeBasePx + C.wakeTravelMul * Math.max(0, traveledPx))
}

/** 騎士の壁ぎわの駆け抜け (K-4) で主人公の背中側へ回った時: 崩れている主人公は騎士の方へ向き直る (騎士が 30px 抜けた所で)。
 *  崩れている間は向きが固定 (player.ts) なので、ここで直さないと背中から突進を受け、弾いた火花も反対側に出る。 */
export function execUniqueFaceStunned(player: Player, boss: Boss): Player {
  const plan = boss.currentAttack?.uniqueExec
  if (!plan || plan.boss !== 'vigil-knight' || !plan.pass || player.state !== 'stunned') return player
  const dx = boss.x - player.x
  if (Math.abs(dx) < 30) return player
  const want: Player['facing'] = dx < 0 ? 'left' : 'right'
  return player.facing === want ? player : { ...player, facing: want }
}

/** 突進の当たり・戻りの間の、当たった所 (active の頭) からの体の動き (前へ +px)。当たったら勢いで少し滑って止まる・弾かれたら後ろへのけぞる。
 *  afterMs = active の頭からの経過 (recovery は active の長さを足す)。 */
export function knightChargeAfterShiftPx(plan: KnightBackturnPlan, afterMs: number): number {
  const C = KNIGHT_CHARGE
  const easeOut = (t: number) => { const p = Math.max(0, Math.min(1, t)); return 1 - (1 - p) * (1 - p) }
  if (plan.parried) return -C.recoilPx * easeOut(afterMs / C.recoilMs)
  return C.brakePx * easeOut(afterMs / C.brakeMs)
}

/** 当たりの前端 (騎士の中心から前へ px)。activeMs = 当たりに入ってからの経過。光の帯の前端 + 15。 */
export function knightBladeFrontPx(activeMs: number): number {
  const K = KNIGHT_BACKTURN
  const p = Math.max(0, Math.min(1, activeMs / K.bladeGrowMs))
  const e = 1 - (1 - p) * (1 - p)
  return KNIGHT_EXEC_TIP_FWD_PX + (K.bladeReachPx - KNIGHT_EXEC_TIP_FWD_PX) * e
}
export function knightStrikeFrontPx(activeMs: number): number {
  return knightBladeFrontPx(activeMs) + KNIGHT_BACKTURN.reachMarginPx
}

// ================================ 花嫁 ================================

/** 新しいキーポーズ (10-09 Codex・窓口が合格・髪飾りなし・scripts/chroma_extract.py で抜いて bbox で切った物)。B1〜B4 = 設計図 §4-3。
 *  元 = LUNAIRE-notes/assets/2026-10-09-exec-motion-art/raw/ の bride_exec_found_v2 / glide_v2 / coil_v2 / bride_okaeri_v3。 */
export const BRIDE_EXEC_ART = {
  found: 'exec_found_1009',      // B1 見つけた (ヴェールの縁を整え・片手は胸)
  glide: 'exec_glide_1009',      // B2 寄っていく (浅く前へ・腕は後ろへ)
  coil: 'exec_coil_1009',        // B3 紫の止まり (縮んで前傾・腕を後ろへ・頭頂が前)
  dive: 'exec_okaeri_1009',      // B4 飛び込み = 当たり (頭から胸へ)
  /** 弾かれた瞬間 (10-12 Codex GPT-6.1 Sol・発注 = .ai-team/inbox/bride-parried-snap-1012.json・元 = LUNAIRE-notes/assets/2026-10-12-exec-unique-fix/art/)。 */
  parriedSnap: 'exec_parried_snap_1012',
} as const
/** 花嫁の新しい4枚の置き方: 大きさは今の花嫁と同じ固定の縮尺 (idle の bbox 高さ 1262 = 表示 168px・頭の大きさで idle とほぼ同じと確かめた)。
 *  anchorX = 裾の下端の真ん中 + 53 (idle の裾の真ん中は bbox の中心より 53px 前 = 同じ足元に立つ)。 */
export const BRIDE_EXEC_ART_PLACEMENT: Readonly<Record<string, { anchorX: number }>> = {
  [BRIDE_EXEC_ART.found]: { anchorX: 513 },
  [BRIDE_EXEC_ART.glide]: { anchorX: 724 },
  [BRIDE_EXEC_ART.coil]: { anchorX: 726 },
  [BRIDE_EXEC_ART.dive]: { anchorX: 923 },
  // 弾かれた瞬間: 前へ広がった裾は支えでないので裾の真ん中は使わず、腰 (帯の下・x 1075) から待機と同じだけ後ろ (+130) に立てる。
  [BRIDE_EXEC_ART.parriedSnap]: { anchorX: 1205 },
}
/** B4 の頭の前端 (絵の画素 x=0) の、体の中心からの前への距離 (表示 168px・倍率 1)。923 × 168 / 1262。 */
export const BRIDE_DIVE_HEAD_FWD_PX = BRIDE_EXEC_ART_PLACEMENT[BRIDE_EXEC_ART.dive]!.anchorX * 168 / 1262
/** 絵の頭のてっぺん (絵の画素)。♡はここのすぐ上から生まれる (髪飾りは無い・ヴェールに包まれた頭のてっぺん)。 */
export const BRIDE_HEAD_TOP_SRC: Readonly<Record<string, readonly [number, number]>> = {
  [BRIDE_EXEC_ART.found]: [342, 0],
  [BRIDE_EXEC_ART.glide]: [130, 0],
  [BRIDE_EXEC_ART.coil]: [110, 0],
  // 10-23 描き直し (開発版 ?brideRedraw=exec・src/bride_redraw_1023.ts)。キーはファイルの幹。
  exec_found_1023: [380, 0],
  exec_glide_1023: [148, 0],
  exec_coil_1023: [132, 0],
  // 10-25 案A の線の描き直し (開発版 ?brideArt=a・src/bride_art_sample_1025.ts)。
  exec_found_art_1025_a: [371, 0],
  exec_glide_art_1025_a: [147, 0],
  exec_coil_art_1025_a: [131, 0],
  idle: [329, 1],
}

export const BRIDE_OKAERI = {
  foundMs: 300,
  /** 寄っていく: 0.65 秒 (遠い時は最大 0.9 秒まで延ばす)。弾みは2回 (上へ 6px)。 */
  glideMs: 650,
  glideMaxMs: 900,
  /** 崩れたら打ち切る (既定・src/stun_cut.ts) 時の短い拍: 見つけた 300→200・寄る 650〜900→550〜750
   *  (技の途中で崩れた時の戻り 0.2 秒を足しても 1.8 秒に収める・前は待機から 1.68 秒・技の途中で 1.92 秒)。旧 (DEV ?stunCut=0) は上の拍。 */
  cut: { foundMs: 200, glideMs: 550, glideMaxMs: 750 },
  glideMaxSpeedPxPerSec: 450,
  hopPx: 6,
  hops: 2,
  /** 紫の止まり (花嫁は 350ms 固定)。 */
  holdMs: 350,
  /** 飛び込み (だんだん速く・最後に一気に届く)。 */
  diveMs: 220,
  diveDistancePx: 110,
  /** 飛び込みが届いた時の間合い = B4 の頭の前端が主人公の中心を 12px 越えた所 (胸に届く)。 */
  landingOverlapPx: 12,
  /** 当たった後、覆い被さる (exec_cover) 時は今の抱擁の間合い。弾かれた時は寄らない。
   *  10-09 夜: 前は当たりの瞬間に exec_cover へ替えて 100ms で 56px 寄せていた。当たりの止め (150ms) の間ずっと
   *  覆い被さる絵が主人公の後ろ (頭が 86px 後ろへ跳ぶ) に出て、止めが明けてから床を 68px 滑っていた。
   *  今: 止めの間は飛び込みの絵 (頭が胸に届いた所) を保ち、止めが明けた最初のコマ (当たりから 1 コマ = 16.7ms) で抱擁の間合いの exec_cover へ替える
   *  (頭の跳び 30px・腰 26px・滑りなし)。 */
  coverGapPx: BOSS_EXECUTE_APPROACH_PX,
  coverAtMs: 16,
  /** ♡: 見つけた時に1つ (120ms)・弾みの頂上で1つずつ。生まれて 350ms で薄れ始め 600ms で消える。止まりの入りに残っている♡は弾けて光の粒に。 */
  heartFoundAtMs: 120,
  heartFadeStartMs: 350,
  heartLifeMs: 600,
  heartRisePx: 30,
  heartPopMs: 80,
  heartBurstMs: 200,
} as const

export type BrideOkaeriPlan = {
  boss: 'bride'
  startGapPx: number
  stopGapPx: number
  landingGapPx: number
  glideStartMs: number
  glideMs: number
  /** 弾みの頂上の時刻 (♡が生まれる)。 */
  hopPeakMs: number[]
  heartBirthMs: number[]
  holdStartMs: number
  diveStartMs: number
  chargeMs: number
  /** 致命の当たりをジャストで弾かれた (hits.ts が当たりの瞬間に立てる・覆い被さらず「弾かれた姿」へ替える)。 */
  parried?: boolean
}

export function planBrideOkaeri(startGapPx: number): BrideOkaeriPlan {
  const B = BRIDE_OKAERI
  const landingGapPx = BRIDE_DIVE_HEAD_FWD_PX - B.landingOverlapPx
  const stopGapPx = landingGapPx + B.diveDistancePx
  const dist = Math.abs(stopGapPx - startGapPx)
  const T = stunCutOn() ? B.cut : B
  const glideMs = Math.round(Math.min(T.glideMaxMs, Math.max(T.glideMs, dist / B.glideMaxSpeedPxPerSec * 1000)))
  const glideStartMs = T.foundMs
  const hopMs = glideMs / B.hops
  const hopPeakMs = Array.from({ length: B.hops }, (_, k) => Math.round(glideStartMs + hopMs * (k + 0.5)))
  const holdStartMs = glideStartMs + glideMs
  const diveStartMs = holdStartMs + B.holdMs
  return { boss: 'bride', startGapPx, stopGapPx, landingGapPx, glideStartMs, glideMs, hopPeakMs,
    heartBirthMs: [B.heartFoundAtMs, ...hopPeakMs], holdStartMs, diveStartMs, chargeMs: diveStartMs + B.diveMs }
}

// ================================ 共通 ================================

export type ExecUniquePlan = KnightBackturnPlan | BrideOkaeriPlan

export const EXEC_UNIQUE_BASE_IDS: Readonly<Record<string, string>> = {
  'vigil-knight': 'knightExecution',
  bride: 'brideExecution',
}

/** スイッチが入っている時の致命 = 今の致命の写しに、この回の段取りと溜めの長さ (騎士は伸びる当たり) を持たせた物。他は undefined。 */
export function execUniqueAttack(bossId: string, base: AttackDef, startGapPx: number, roomBehindPx: number,
  roomPastPlayerPx = Infinity): AttackDef | undefined {
  if (!execUniqueOn(bossId) || EXEC_UNIQUE_BASE_IDS[bossId] !== base.id) return undefined
  if (bossId === 'vigil-knight') {
    const plan = planKnightBackturn(startGapPx, roomBehindPx, knightForm, roomPastPlayerPx)
    // 突進: 当たりの前端 = K3 の穂先 + 15 (穂先が届いた所 = 当たり・光の尾は後ろへ伸びるので当たりを伸ばさない)。band = 光の帯の届き切り + 15。
    const front = plan.form === 'charge'
      ? Math.round(KNIGHT_EXEC_TIP_FWD_PX + KNIGHT_BACKTURN.reachMarginPx)
      : KNIGHT_BACKTURN.bladeReachPx + KNIGHT_BACKTURN.reachMarginPx
    return { ...base, chargeMs: plan.chargeMs, range: front, approachDistancePx: front,
      strike: { localFromPx: KNIGHT_BACKTURN.strikeFromPx, localToPx: front }, uniqueExec: plan }
  }
  // 当たりの前端 = B4 の頭の前端 + 15 (後端は今のまま)。
  const plan = planBrideOkaeri(startGapPx)
  const front = Math.round(BRIDE_DIVE_HEAD_FWD_PX + 15)
  return { ...base, chargeMs: plan.chargeMs, range: front, approachDistancePx: front,
    strike: { localFromPx: base.strike?.localFromPx ?? 20, localToPx: front }, uniqueExec: plan }
}

function easeInOut(t: number): number {
  const p = Math.max(0, Math.min(1, t))
  return p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)
}
function easeIn(t: number): number { const p = Math.max(0, Math.min(1, t)); return p * p }

/** 花嫁の当たりの後 (active の頭から ms): 止めの間は飛び込みの間合い、明けたら今の抱擁の間合い (絵が exec_cover へ替わるのと同じコマ)。弾かれた時は呼ばない。 */
/** 弾かれた花嫁 (?execUniqueBrideRise 指定なし = glide) が、当たった所から後ろへ押し返される量 (px・正 = 主人公から離れる)。
 *  afterMs = active の頭からの経過 (recovery は active の長さを足す)。old は動かない (0)。 */
/** 弾かれた花嫁 (glide) の体の動き。px は正 = 後ろへ・負 = 前へ。10-12 統合係の検収で「後ろへ 44px 押し返す」をやめ、
 *  飛び込んだ勢いで裾が少し前へ流れる (頭は上体が起きる分だけ下がる) にした。下がりの合計 = 待機の頭 − 飛び込みの頭 ≒ 84 + px。 */
export const BRIDE_PARRIED_SLIDE = { px: -14, startMs: 30, durMs: 600 } as const
/** 弾かれた花嫁 (glide) の絵の描き位置 (前へ +px)・傾き (+ = 後ろへ起きる)・時刻 (recovery 900ms の割合)。 */
export const BRIDE_PARRIED_POSE = {
  glideFromPx: 16, glideMidPx: 9, glideMidRot: 5, glideMidLiftPx: 5, glideRot: 12, glideLiftPx: 12, glideEndAt: 0.45,
  idleAt: 0.5, idleFromPx: 14, idleSinkPx: 4, idleSettleAt: 0.85,
} as const
/** 弾かれた花嫁 (snap・既定) の絵の描き位置 (前へ +px)・沈み (+ = 下へ)・傾き (+ = 後ろへ起きる)・時刻 (active 300ms / recovery 900ms の割合)。
 *  値は tests/exec_unique_measure.test.ts で頭の位置を 1 コマずつ測って決めた (記録 docs/research/2026-10-12-exec-unique-fix.md)。 */
export const BRIDE_PARRIED_SNAP = {
  /** 弾かれた瞬間 (当たりの止めが明けるコマ): 専用の絵へ替え、頭はまだ飛び込みの頭のすぐ後ろ。 */
  impactAt: 0.12, impactFwdPx: 92, impactSinkPx: 18, impactRot: 5,
  /** 跳ね返り (速い動き): 0.11 秒で頭が後ろへ・沈みが戻り・反りが揃う。 */
  recoilAt: 0.5, holdFwdPx: 34,
  /** 止め: recovery の頭 0.09 (約 80ms) まで、ほぼ動かない (2px だけ沈む)。 */
  holdEndAt: 0.09, holdSinkPx: 2,
  /** 戻り: 上体を少し前へ戻して (傾き −2°)、待機の絵へ替える。替わる時に頭が続くよう描き位置をずらし、0 へ戻す。 */
  riseAt: 0.36, riseFwdPx: 30, riseRot: -2, riseLiftPx: 14,
  idleFromPx: 18, idleSinkPx: 17, idleSettleAt: 0.78,
  /** 裾が前へ流れる (glide と同じ 14px) のは戻りの間だけ (止めの間は動かさない)。当たりの頭からの ms。 */
  slide: { px: -14, startMs: 400, durMs: 420 },
} as const
export function brideParriedShiftPx(plan: BrideOkaeriPlan, afterMs: number): number {
  if (!plan.parried || brideRise === 'old') return 0
  const S = brideRise === 'snap' ? BRIDE_PARRIED_SNAP.slide : BRIDE_PARRIED_SLIDE
  const p = Math.max(0, Math.min(1, (afterMs - S.startMs) / S.durMs))
  return S.px * p * p * (3 - 2 * p)
}

export function brideCoverGapAt(plan: BrideOkaeriPlan, activeMs: number): number {
  return activeMs + 1e-6 < BRIDE_OKAERI.coverAtMs ? plan.landingGapPx : BRIDE_OKAERI.coverGapPx
}

export type ExecUniquePose = {
  /** ボスと主人公の間合い (px)。 */
  gapPx: number
  /** 主人公と逆を向く (歩き去る絵だけ)。 */
  faceAway: boolean
  /** 主人公のどちら側にいるか (1 = 溜めの頭と同じ側・-1 = 壁ぎわの駆け抜けで反対側へ回った後)。省略 = 1。 */
  side?: 1 | -1
}

/** 溜めの頭から elapsedMs の時の間合いと向き。 */
export function execUniquePoseAt(plan: ExecUniquePlan, elapsedMs: number): ExecUniquePose {
  // 区切りの時刻ちょうど (浮動小数で 249.99999…) は、絵の並び (selectTimelineFrame は 1e-9 の余裕で次の絵を選ぶ) と同じ側へ倒す。
  //   10-09 夜: 歩き出しの 1 コマだけ、歩きの絵が主人公の方を向いて出ていた (穂先が 230px 跳んで戻る)。
  const t = elapsedMs + 1e-6
  if (plan.boss === 'vigil-knight' && plan.pass) {
    // 壁ぎわの駆け抜け (K-4): 主人公の方を向いたまま走り出し、脇を抜けて反対側へ。抜けた後は主人公に背を向けて走り、止まる。
    //   止まった後 (紫の止まり・振り向き・突進) は反対側で、普段と同じ間合いの数え方。
    const ps = plan.pass
    if (t < ps.endMs) {
      const s = plan.startGapPx - (plan.startGapPx + ps.toGapPx) * knightPassShare((t - ps.startMs) / (ps.endMs - ps.startMs))
      return { gapPx: Math.abs(s), faceAway: s < 0, side: s >= 0 ? 1 : -1 }
    }
    return { ...knightPoseAfterHold(plan, t), side: -1 }
  }
  if (plan.boss === 'vigil-knight') {
    for (const s of plan.steps) {
      if (t < s.startMs) break
      if (t < s.endMs) {
        const p = (t - s.startMs) / (s.carryEndMs - s.startMs)
        return { gapPx: s.fromGapPx + (s.toGapPx - s.fromGapPx) * easeInOut(p), faceAway: s.away }
      }
    }
    if (t < plan.holdStartMs) {
      const last = plan.steps[plan.steps.length - 1]
      const before = plan.steps.length && t < plan.steps[0]!.startMs
      return { gapPx: last && !before ? last.toGapPx : plan.startGapPx, faceAway: false }
    }
    return knightPoseAfterHold(plan, t)
  }
  if (t < plan.glideStartMs) return { gapPx: plan.startGapPx, faceAway: false }
  if (t < plan.holdStartMs) {
    // 滑る (弾みのたびに少し前へ加速 = 弾みごとに easeInOut)。
    const hopMs = plan.glideMs / BRIDE_OKAERI.hops
    const u = (t - plan.glideStartMs) / hopMs
    const k = Math.min(BRIDE_OKAERI.hops - 1, Math.floor(u))
    const done = (k + easeInOut(u - k)) / BRIDE_OKAERI.hops
    return { gapPx: plan.startGapPx + (plan.stopGapPx - plan.startGapPx) * done, faceAway: false }
  }
  if (t < plan.diveStartMs) return { gapPx: plan.stopGapPx, faceAway: false }
  const p = (t - plan.diveStartMs) / (plan.chargeMs - plan.diveStartMs)
  return { gapPx: plan.stopGapPx + (plan.landingGapPx - plan.stopGapPx) * easeIn(p), faceAway: false }
}

/** 騎士の紫の止まりの入りから後 (止まり・振り向き・突進/振り抜き)。t = 溜めの頭からの ms。 */
function knightPoseAfterHold(plan: KnightBackturnPlan, t: number): ExecUniquePose {
  if (t < plan.turnMidStartMs) return { gapPx: plan.holdGapPx, faceAway: false }
  if (plan.form === 'charge') {
    // 振り向きの途中 (K2): 軸足を残して 12px だけ前へ → 走る (加速しながら一気に詰める)。
    if (t < plan.runStartMs) {
      const p = (t - plan.turnMidStartMs) / (plan.runStartMs - plan.turnMidStartMs)
      return { gapPx: plan.holdGapPx + (plan.runFromGapPx - plan.holdGapPx) * easeIn(p), faceAway: false }
    }
    if (t < plan.chargeMs) {
      const p = (t - plan.runStartMs) / (plan.chargeMs - plan.runStartMs)
      return { gapPx: plan.runFromGapPx + (plan.strikeGapPx - plan.runFromGapPx) * knightChargeRunShare(p), faceAway: false }
    }
    return { gapPx: plan.strikeGapPx, faceAway: false }
  }
  if (t < plan.chargeMs) {
    const p = (t - plan.turnMidStartMs) / (plan.chargeMs - plan.turnMidStartMs)
    return { gapPx: plan.holdGapPx + (plan.strikeGapPx - plan.holdGapPx) * easeIn(p), faceAway: false }
  }
  return { gapPx: plan.strikeGapPx, faceAway: false }
}

/** 壁ぎわの駆け抜けの走りの絵 (溜めの頭から ms)。駆け抜けていない時は undefined。走りの絵は進んだ距離 90px ごとに替える。 */
export function knightPassRunImageAt(plan: KnightBackturnPlan, ms: number): 'charge_run1' | 'charge_run2' | undefined {
  const ps = plan.pass
  if (!ps || ms < ps.startMs || ms >= ps.endMs) return undefined
  const share = knightPassShare((ms - ps.startMs) / (ps.endMs - ps.startMs))
  if (share >= KNIGHT_WALL_PASS.brakeShare) return undefined
  const traveled = (plan.startGapPx + ps.toGapPx) * share
  return Math.floor(traveled / KNIGHT_CHARGE.strideImagePx) % 2 === 0 ? 'charge_run1' : 'charge_run2'
}

/** 止まりの入りの時刻 (一瞬の静止と紫が一気に濃くなる時刻)。 */
export function execUniqueSignalMs(plan: ExecUniquePlan): number { return plan.holdStartMs }

/** この tick で止まりの入りを跨いだか (一瞬の静止 120ms を出す)。 */
export function execUniqueSignalCrossed(plan: ExecUniquePlan, prevElapsedMs: number, nextElapsedMs: number): boolean {
  return prevElapsedMs < plan.holdStartMs && nextElapsedMs >= plan.holdStartMs
}

/** 紫のオーラの強さ (溜めの間)。寄っていく・歩き去る間は薄く (35%)、止まりの入りで一気に濃く (60ms)、止まりの間は保つ。 */
export function execUniqueAuraK(plan: ExecUniquePlan, telegraphElapsedMs: number): number {
  const t = telegraphElapsedMs
  if (t < plan.holdStartMs) return 0.35
  return 0.35 + 0.65 * Math.min(1, (t - plan.holdStartMs) / 60)
}

// ===== 絵の並び (タイムライン) =====
type Motion = Pick<BossPoseKey, 'offset' | 'scale' | 'rot' | 'ease' | 'hold'>
const HOLD = { hold: true } as const
const timelineCache = new WeakMap<ExecUniquePlan, BossAttackTimeline>()

/** turn の絵の描き位置 (前へ +px)。turn は bbox の中心に立つと両足の真ん中が待機より 20px 後ろへずれる (tests/exec_unique_measure.test.ts で測った) ので、
 *  前後の絵 (待機・歩き・K3) との跳びを半分ずつに分ける。 */
const KNIGHT_TURN_OFFSET_PX = 12
/** 背を向けて歩き出す1歩目の描き始め (前へ +px・10-09 夜に測って決めた)。-3 なら turn の兜にぴったり続くが1歩目の足が 91px 滑る・
 *  前の 23 (待機の兜) なら滑り 65px で兜が 27px 跳ぶ。10 = 兜の跳び約 13px・滑り約 78px (2歩目以降の1歩と同じ)。 */
const KNIGHT_WALK_FROM_TURN_OFFSET_PX = 10

const KNIGHT_STRIKE: BossAttackTimeline = [
  { phase: 'active', at: 0.0, image: KNIGHT_EXEC_ART.backturn, tag: 'contact', hold: true, offset: { x: -4 }, scale: { x: 1.03, y: 0.99 }, ease: 'easeOut' },
  { phase: 'active', at: 0.5, image: KNIGHT_EXEC_ART.backturn, hold: true, offset: { x: 0 }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.0, image: KNIGHT_EXEC_ART.backturn, tag: 'followThrough', hold: true, offset: { x: 0 }, ease: 'step' },
]
/** 騎士の当たり・戻りの絵 (前の版 ?execUniqueRecover=turn): K3 を保って振り抜き → 扉の方へ向き直る → 待機。 */
const KNIGHT_AFTER_TURN: BossAttackTimeline = [
  ...KNIGHT_STRIKE,
  { phase: 'recovery', at: 0.42, image: 'turn', hold: true, ease: 'step', offset: { x: KNIGHT_TURN_OFFSET_PX }, scale: { x: 0.99, y: 1.0 } },
  { phase: 'recovery', at: 0.78, image: 'idle', hold: true },
]
/** 騎士の当たり・戻りの絵 (既定 guard・10-09 夜): K3 を保って振り抜き → 槍を主人公へ向けたまま構えを引く (walk_reach) → 待機。
 *  walk_reach の足幅 (103px) は K3 (185px) と待機 (34px) の間なので、1コマで足が跳ぶ量を半分ずつに分けられる。描き位置は両方の跳びが揃う所。 */
const KNIGHT_GUARD_OFFSET_PX = 18
const KNIGHT_AFTER_GUARD: BossAttackTimeline = [
  ...KNIGHT_STRIKE,
  { phase: 'recovery', at: 0.42, image: 'walk_reach', hold: true, ease: 'step', offset: { x: KNIGHT_GUARD_OFFSET_PX }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.7, image: 'idle', hold: true, offset: { x: 0 } },
]
/** 弾かれた時 (10-09 夜): 振り抜いた K3 (当たりの止めの間) → 弾かれた姿 (parried・槍が上へ跳ね上がる) → 待機。背を向け直したりしない。
 *  前は弾かれても致命の絵のまま揺れて、そのまま扉の方へ背を向けていた (勝った後の残心に見える)。 */
const KNIGHT_PARRIED_OFFSET_PX = -8
const KNIGHT_AFTER_PARRIED: BossAttackTimeline = [
  { phase: 'active', at: 0.0, image: KNIGHT_EXEC_ART.backturn, tag: 'contact', hold: true, offset: { x: -4 }, scale: { x: 1.03, y: 0.99 }, ease: 'step' },
  { phase: 'active', at: 0.12, image: 'parried', hold: true, ease: 'step', offset: { x: KNIGHT_PARRIED_OFFSET_PX }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.0, image: 'parried', hold: true, ease: 'step', offset: { x: KNIGHT_PARRIED_OFFSET_PX } },
  { phase: 'recovery', at: 0.6, image: 'idle', hold: true, offset: { x: 0 } },
]
/** 弾かれた時 (10-20 作者「弾いた瞬間にのけぞってないといけない」・src/exec_parry_recoil.ts): 弾いたコマ (止めの最初) から弾かれた姿。
 *  前 (KNIGHT_AFTER_PARRIED・DEV ?execParryRecoil=old) は当たりの 12% まで振り抜いた K3 のままで、スローの半ばまで弾かれた姿が出なかった。 */
const KNIGHT_AFTER_PARRIED_NOW: BossAttackTimeline = [
  { phase: 'active', at: 0.0, image: 'parried', tag: 'contact', hold: true, ease: 'step', offset: { x: KNIGHT_PARRIED_OFFSET_PX }, scale: { x: 1, y: 1 } },
  ...KNIGHT_AFTER_PARRIED.slice(2),
]
function knightAfter(plan: KnightBackturnPlan): BossAttackTimeline {
  if (plan.parried) return execParryRecoilNow() ? KNIGHT_AFTER_PARRIED_NOW : KNIGHT_AFTER_PARRIED
  return knightRecover === 'turn' ? KNIGHT_AFTER_TURN : KNIGHT_AFTER_GUARD
}
/** 花嫁の当たり・戻り (10-09 夜): 当たりの止めの間は飛び込み (頭が胸に届いた所) を保ち、止めが明けたら抱擁 (exec_cover)。
 *  弾かれた時は覆い被さらず、弾かれた姿 (parried) → 待機。 */
/** 花嫁の致命の当たりの長さ (attacks.ts brideExecution.activeMs・tests/exec_unique.test.ts が同じか見張る)。 */
export const BRIDE_EXEC_ACTIVE_MS = 300
function brideAfter(plan: BrideOkaeriPlan, base: BossAttackTimeline): BossAttackTimeline {
  const coverAt = BRIDE_OKAERI.coverAtMs / BRIDE_EXEC_ACTIVE_MS
  const dive: BossPoseKey = { phase: 'active', at: 0.0, image: BRIDE_EXEC_ART.dive, tag: 'contact', hold: true, ease: 'step' }
  if (plan.parried && brideRise === 'snap') {
    // 10-12 (ユーザー B-2): 弾かれた専用の絵へ替えて「速く跳ね返る → 止め → 戻る」。前 (glide) は寄っていく絵のまま 0.45 秒かけて起きていた。
    const S = BRIDE_PARRIED_SNAP
    // 10-20 (作者「弾いた瞬間にのけぞってないといけない」・src/exec_parry_recoil.ts): 弾いたコマ (止めの最初) から専用の絵。
    //   跳ね返りの長さ (impactAt→recoilAt) はそのまま前へずらす。前 (DEV ?execParryRecoil=old) は当たりの 12% (スローの終わり近く) まで飛び込みの絵だった。
    const now = execParryRecoilNow()
    const impactAt = now ? 0 : S.impactAt
    const recoilAt = now ? S.recoilAt - S.impactAt : S.recoilAt
    const impact: BossPoseKey = { phase: 'active', at: impactAt, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'easeOut',
      offset: { x: S.impactFwdPx, y: S.impactSinkPx }, scale: { x: 1, y: 1 }, rot: S.impactRot }
    return [...(now ? [{ ...impact, tag: 'contact' as const }] : [dive, impact]),
      { phase: 'active', at: recoilAt, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'linear', offset: { x: S.holdFwdPx, y: 0 }, rot: 0 },
      // 相 (active / recovery) の境目は同じ値でつなぐ (補間は相の中だけ・glide と同じ)。
      { phase: 'active', at: 0.999, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'step', offset: { x: S.holdFwdPx, y: S.holdSinkPx * 0.6 }, rot: 0 },
      { phase: 'recovery', at: 0.0, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'linear', offset: { x: S.holdFwdPx, y: S.holdSinkPx * 0.6 }, rot: 0 },
      { phase: 'recovery', at: S.holdEndAt, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'easeInOut', offset: { x: S.holdFwdPx, y: S.holdSinkPx }, rot: 0 },
      // 待機の絵は頭が 33px 高いので、替わる前に上体を少し持ち上げ (14px)、替わった待機は沈めてから (17px) 立たせる。
      { phase: 'recovery', at: S.riseAt - 0.001, image: BRIDE_EXEC_ART.parriedSnap, hold: true, ease: 'step', offset: { x: S.riseFwdPx, y: -S.riseLiftPx }, rot: S.riseRot },
      { phase: 'recovery', at: S.riseAt, image: 'idle', hold: true, ease: 'easeInOut', offset: { x: S.idleFromPx, y: S.idleSinkPx }, rot: 0 },
      { phase: 'recovery', at: S.idleSettleAt, image: 'idle', hold: true, ease: 'step', offset: { x: 0, y: 0 }, rot: 0 }]
  }
  if (plan.parried && brideRise === 'glide') {
    // 10-12 (ユーザー A-5「強すぎる」): 弾かれた姿 (上体を大きく反らす・頭が 117px 後ろへ跳ぶ) をやめ、
    //   寄っていく絵 (腕とヴェールが後ろへ流れる) のまま上体がゆっくり起き、飛び込んだ勢いで裾は少し前へ流れる (位置は brideParriedShiftPx・boss.ts)。
    //   10-12 統合係の検収: 待機へ戻る 1 コマで頭が 59px 跳んでいた・下がりの合計 128px (前の版 117px より大きい) を直した。
    //   どの切り替わりも、前の絵の頭に続くよう描き位置を前へずらしてから 0 へ戻す (BRIDE_PARRIED_POSE・tests/exec_unique_measure.test.ts で測った)。
    const P = BRIDE_PARRIED_POSE
    return [dive,
      { phase: 'active', at: 0.12, image: BRIDE_EXEC_ART.glide, hold: true, ease: 'linear', offset: { x: P.glideFromPx, y: 0 }, rot: 0 },
      // 描き位置と傾きは相 (active / recovery) の中でしか補間されないので、active の終わりと recovery の頭に同じ値を置いてつなぐ
      //   (置かないと recovery の頭の 1 コマで頭が 30px 跳んだ・実機の 1 コマずつの記録で見つけた)。
      { phase: 'active', at: 0.999, image: BRIDE_EXEC_ART.glide, hold: true, ease: 'step', offset: { x: P.glideMidPx, y: -P.glideMidLiftPx }, rot: P.glideMidRot },
      { phase: 'recovery', at: 0.0, image: BRIDE_EXEC_ART.glide, hold: true, ease: 'linear', offset: { x: P.glideMidPx, y: -P.glideMidLiftPx }, rot: P.glideMidRot },
      { phase: 'recovery', at: P.glideEndAt, image: BRIDE_EXEC_ART.glide, hold: true, ease: 'step', offset: { x: 0, y: -P.glideLiftPx }, rot: P.glideRot },
      { phase: 'recovery', at: P.idleAt, image: 'idle', hold: true, ease: 'easeInOut', offset: { x: P.idleFromPx, y: P.idleSinkPx }, rot: 0 },
      { phase: 'recovery', at: P.idleSettleAt, image: 'idle', hold: true, ease: 'step', offset: { x: 0, y: 0 }, rot: 0 }]
  }
  if (plan.parried) {
    return [dive,
      { phase: 'active', at: 0.12, image: 'parried', hold: true, ease: 'step' },
      { phase: 'recovery', at: 0.0, image: 'parried', hold: true, ease: 'step' },
      { phase: 'recovery', at: 0.55, image: 'idle', hold: true }]
  }
  const after = base.filter((k) => k.phase !== 'telegraph').map((k) => {
    if (k.phase !== 'active' || k.at !== 0) return k
    const { tag: _tag, ...rest } = k
    return { ...rest, at: coverAt }
  })
  return [dive, ...after]
}

/** この回の段取りの絵の並び。花嫁の当たりの後は brideAfter (止めの間は飛び込み → 今の brideExecution の exec_cover・弾かれたら parried)。 */
export function execUniqueTimeline(plan: ExecUniquePlan, base: BossAttackTimeline): BossAttackTimeline {
  const cached = timelineCache.get(plan)
  if (cached) return cached
  const keys: BossPoseKey[] = []
  const at = (ms: number) => Math.max(0, Math.min(0.999999, ms / plan.chargeMs))
  const key = (ms: number, image: string, m: Motion = {}) => keys.push({ phase: 'telegraph', at: at(ms), image, ...HOLD, ...m })
  let out: BossAttackTimeline
  if (plan.boss === 'vigil-knight') {
    const K = KNIGHT_BACKTURN
    const approach = plan.steps.length > 0 && !plan.steps[0]!.away
    if (!approach) key(0, 'idle', { offset: { x: 0 }, ease: 'step' })
    // 歩き (open の歩きと同じ絵: 運ぶ間 = 広い構え walk_reach・置いた後 = 揃えかけ walk_passing)。
    //   今の歩きと同じく、運ぶ間は兜の高さ・位置がつながるように絵の描き位置を前の構えの兜 → 揃えかけの兜へ寄せる
    //   (src/knight_gait.ts knightGaitDrawOffsetPx の open・body と同じ式。体の位置の進み方と同じ easeInOut)。
    const walk = () => {
      const helmet = knightWalkHelmetPx()
      plan.steps.forEach((s, i) => {
        // 1歩目の描き始め: 主人公の方へ歩く時は待機の兜から、背を向けて歩き去る時は turn の兜から続ける
        //   (10-09 夜: 背を向けて歩き出す1コマで兜が 27px・腰が 24px 跳んでいた = 待機の兜に合わせていたため。10 = 跳びと1歩目の滑りの釣り合い)。
        const from0 = i === 0 ? (approach ? helmet.idle - helmet.walk_reach : KNIGHT_WALK_FROM_TURN_OFFSET_PX) : helmet.walk_passing - helmet.walk_reach
        key(s.startMs, 'walk_reach', { offset: { x: from0 }, scale: { x: 1, y: 1 }, ease: 'easeInOut' })
        key(s.carryEndMs - 1, 'walk_reach', { offset: { x: helmet.walk_passing - helmet.walk_reach }, scale: { x: 1, y: 1 }, ease: 'step' })
        key(s.carryEndMs, 'walk_passing', { offset: { x: 0 }, scale: { x: 1, y: 1 }, ease: 'step' })
      })
    }
    if (plan.pass) {
      // 壁ぎわの駆け抜け (K-4): 待機 → 床を蹴る一瞬 (K2 と同じ低い構えの walk_reach) → 走りの絵 2 枚を進んだ距離 90px ごとに替える。
      //   向き (facing) は進む向きのまま (主人公の脇を抜けるまでは主人公の方・抜けた後は主人公と逆 = execUniquePoseAt)。
      //   蹴り出し = 壁ぎわの突進の溜めと同じ lanceThrust_windup・止まる所 = 壁ぎわの突進の止まりと同じ踏ん張り scrape_stop (進んだ距離の 8 割から)。
      const ps = plan.pass
      key(ps.startMs * 0.3, 'lanceThrust_windup', { offset: { x: 18, y: 0 }, scale: { x: 1, y: 1 }, rot: 0, ease: 'step' })
      const dist = plan.startGapPx + ps.toGapPx
      const brakeFrom = KNIGHT_WALL_PASS.brakeShare * dist
      for (let k = 0, d = 0; d < brakeFrom; k++, d += KNIGHT_CHARGE.strideImagePx) {
        const p = knightPassShareInv(d / dist)
        key(ps.startMs + (ps.endMs - ps.startMs) * p, k % 2 === 0 ? 'charge_run1' : 'charge_run2', { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 }, rot: 0, ease: 'step' })
      }
      key(ps.startMs + (ps.endMs - ps.startMs) * knightPassShareInv(KNIGHT_WALL_PASS.brakeShare), 'scrape_stop',
        { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 }, rot: 0, ease: 'step' })
    } else {
      if (approach) walk()
      // 背を向ける: 待機の絵から turn (背中) へ。turn の絵は画面右を向く = 向き (facing) は主人公へのまま。
      key(plan.turnStartMs + K.turnImageAtMs, 'turn', { offset: { x: KNIGHT_TURN_OFFSET_PX }, scale: { x: 0.98, y: 1.0 }, ease: 'easeOut' })
      key(plan.turnStartMs + K.turnMs - 1, 'turn', { offset: { x: KNIGHT_TURN_OFFSET_PX }, scale: { x: 1, y: 1 }, ease: 'step' })
      if (!approach) walk()
    }
    // 紫の止まり (K1): 背を向けたまま沈んでひねり込む。止まりの間はほとんど動かない (最後の 40% は完全に止める)。
    const h0 = plan.holdStartMs
    key(h0, KNIGHT_EXEC_ART.backcoil, { offset: { x: 0, y: 0 }, scale: { x: 1.0, y: 1.0 }, ease: 'easeOut' })
    key(h0 + plan.holdMs * 0.6, KNIGHT_EXEC_ART.backcoil, { offset: { x: 3, y: 2 }, scale: { x: 1.01, y: 0.985 }, ease: 'step' })
    // 振り向きの途中 (K2): 軸足 (奥の足) を床に残して回る。体は滑らせず (位置は execUniquePoseAt・12px だけ)、
    //   上体を主人公と逆へ少し反らせた所から、振りへ向けて主人公の方へ倒し込む (回転の軸は足元)。
    key(plan.turnMidStartMs, KNIGHT_EXEC_ART.turnmid, { offset: { x: 0, y: 1 }, scale: { x: 0.99, y: 1.0 }, rot: 1.0, ease: 'easeIn' })
    key(plan.runStartMs - 1, KNIGHT_EXEC_ART.turnmid, { offset: { x: 0, y: 0 }, scale: { x: 1.02, y: 0.99 }, rot: -1.5, ease: 'step' })
    if (plan.form === 'charge' && plan.chargeMs > plan.runStartMs) {
      // 突進 (10-12): 今ある壁ぎわの突進の走りの絵 2 枚を、進んだ距離 90px ごとに替える (走り出しは床を蹴る run1)。
      //   絵の描き位置は動かさない (体の位置は execUniquePoseAt・穂先の光の尾は exec_unique_fx.ts がこの絵の穂先から描く)。
      const runPx = plan.runFromGapPx - plan.strikeGapPx
      const runMs = plan.chargeMs - plan.runStartMs
      for (let k = 0, d = 0; d < runPx; k++, d += KNIGHT_CHARGE.strideImagePx) {
        const ms = plan.runStartMs + runMs * knightChargeRunShareInv(d / runPx)
        key(Math.min(plan.chargeMs - 1, ms), k % 2 === 0 ? 'charge_run1' : 'charge_run2', { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 }, rot: 0, ease: 'step' })
      }
    }
    out = [...keys, ...knightAfter(plan)]
  } else {
    const B = BRIDE_OKAERI
    const A = BRIDE_EXEC_ART
    // 見つけた (B1): 小首をかしげてヴェールの縁を整える。
    key(0, 'idle', { ease: 'easeOut' })
    key(80, A.found, { offset: { x: 0, y: 0 }, rot: 0, ease: 'easeInOut' })
    key(plan.glideStartMs - 30, A.found, { offset: { x: 0, y: 1 }, rot: -0.8, ease: 'step' })
    // 寄っていく (B2): 弾みは2回 (上へ 6px)。弾みの頂上で♡。
    const hopMs = plan.glideMs / B.hops
    for (let k = 0; k < B.hops; k++) {
      const s = plan.glideStartMs + hopMs * k
      key(s, A.glide, { offset: { x: 0, y: 0 }, rot: 0, ease: 'easeOut' })
      key(s + hopMs * 0.5, A.glide, { offset: { x: 2, y: -B.hopPx }, rot: 1.0, ease: 'easeIn' })
    }
    // 紫の止まり (B3): 前へ屈み、ドレスの中へ沈む。止まりの間は保つ。
    const h0 = plan.holdStartMs
    key(h0, A.coil, { offset: { x: 0, y: 0 }, scale: { x: 1.0, y: 1.0 }, rot: 0, ease: 'easeOut' })
    key(h0 + B.holdMs * 0.5, A.coil, { offset: { x: -2, y: 2 }, scale: { x: 1.0, y: 0.985 }, rot: -0.5, ease: 'step' })
    // 飛び込み (B4): 前へ倒れ込みながら伸びる (頭から胸へ)。
    key(plan.diveStartMs, A.dive, { offset: { x: -4, y: 0 }, scale: { x: 0.98, y: 1.0 }, rot: -1.0, ease: 'easeIn' })
    key(plan.chargeMs - 1, A.dive, { offset: { x: 0, y: 0 }, scale: { x: 1.0, y: 1.0 }, rot: 0, ease: 'step' })
    out = [...keys, ...brideAfter(plan, base)]
  }
  timelineCache.set(plan, out)
  return out
}

/** 描画・FX 用: 溜めの頭から ms の時に出ている花嫁の代用の絵 (♡の生まれる頭の位置)。 */
export function brideImageAt(plan: BrideOkaeriPlan, ms: number): string {
  if (ms < 80) return 'idle'
  if (ms < plan.glideStartMs) return BRIDE_EXEC_ART.found
  if (ms < plan.holdStartMs) return BRIDE_EXEC_ART.glide
  return BRIDE_EXEC_ART.coil
}

/** 弾みの高さ (♡の生まれる高さに足す・上 = +)。 */
export function brideHopLiftPx(plan: BrideOkaeriPlan, ms: number): number {
  if (ms < plan.glideStartMs || ms >= plan.holdStartMs) return 0
  const hopMs = plan.glideMs / BRIDE_OKAERI.hops
  const u = ((ms - plan.glideStartMs) % hopMs) / hopMs
  return BRIDE_OKAERI.hopPx * (u < 0.5 ? 1 - (1 - 2 * u) ** 2 : 1 - ((2 * u - 1) ** 2))
}

export type HeartState =
  | { kind: 'float'; ageMs: number; alpha: number; scale: number; risePx: number; swayPx: number; driftPx: number }
  | { kind: 'pop'; popMs: number; risePx: number; swayPx: number; driftPx: number; alphaAtPop: number }
  | null

/** ♡ k (0 始まり) の、溜めの頭から ms の時の状態。null = 出ていない。 */
export function heartStateAt(plan: BrideOkaeriPlan, k: number, ms: number): HeartState {
  const B = BRIDE_OKAERI
  const born = plan.heartBirthMs[k]
  if (born === undefined || ms < born) return null
  const motion = (age: number) => {
    const p = Math.min(1, age / B.heartLifeMs)
    return { risePx: B.heartRisePx * (1 - (1 - p) ** 1.6), swayPx: 4 * Math.sin(age / 1000 * Math.PI * 2 * 1.6 + k * 1.7), driftPx: -6 * p }
  }
  const aliveAlpha = (age: number) => age < B.heartFadeStartMs ? 1
    : Math.max(0, 1 - (age - B.heartFadeStartMs) / (B.heartLifeMs - B.heartFadeStartMs))
  // 止まりの入りに残っている♡は、ぽんと弾けて光の粒になる (灰色にしない・10-09)。
  if (ms >= plan.holdStartMs) {
    const ageAtPop = plan.holdStartMs - born
    if (ageAtPop >= B.heartLifeMs) return null
    const popMs = ms - plan.holdStartMs
    if (popMs >= B.heartBurstMs) return null
    return { kind: 'pop', popMs, alphaAtPop: aliveAlpha(ageAtPop), ...motion(ageAtPop) }
  }
  const age = ms - born
  if (age >= B.heartLifeMs) return null
  // 生まれる時に 0 → 1.15 → 1.0 倍へ 120ms で弾ける。
  const s = age < 70 ? 1.15 * easeInOut(age / 70) : age < 120 ? 1.15 - 0.15 * easeInOut((age - 70) / 50) : 1
  return { kind: 'float', ageMs: age, alpha: aliveAlpha(age), scale: s, ...motion(age) }
}

/** 騎士の光の帯の段 (当たりからの経過 ms)。null = 出ていない。 */
export function knightBladePhase(activeMs: number): { frontPx: number; bodyAlpha: number; unravel: number } | null {
  const K = KNIGHT_BACKTURN
  if (activeMs < 0 || activeMs >= K.bladeEndMs) return null
  const bodyAlpha = activeMs < K.bladeFadeStartMs ? 1 : 1 - (activeMs - K.bladeFadeStartMs) / (K.bladeEndMs - K.bladeFadeStartMs)
  return { frontPx: knightBladeFrontPx(activeMs), bodyAlpha, unravel: Math.max(0, (activeMs - K.bladeGrowMs) / (K.bladeEndMs - K.bladeGrowMs)) }
}
/** 床の残り火の明るさ (当たりからの経過 ms)。0 = 出ない。 */
export function knightEmbersLevel(activeMs: number): number {
  const K = KNIGHT_BACKTURN
  if (activeMs < K.embersStartMs || activeMs >= K.embersEndMs) return 0
  const p = (activeMs - K.embersStartMs) / (K.embersEndMs - K.embersStartMs)
  return p < 0.12 ? p / 0.12 : (1 - (p - 0.12) / 0.88) ** 1.3
}
